import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

export interface QueryGetter {
  get: (param: string) => string | undefined;
  getAll: (param: string) => string[];
  getInt: (param: string) => number | undefined;
  getDate: (param: string) => Date | undefined;
  getNumber: (param: string) => number | undefined;
  getBoolean: (param: string) => boolean | undefined;
}

export interface QueryUpdater {
  (params: { [key: string]: string | number | Date | undefined }, replace?: boolean): void;
}

/**
 * Retrieve and set query parameters for react-router based apps.
 *
 * Note that I considered using the `use-query-params` library from NPM instead
 * of rewriting this functionality from scratch, but that library is HUGE
 * for what it does, and is around 30K larger than this implementation.
 *
 * @example
 *    const [query, setQuery] = useQueryParams();
 *
 *    const zoom = query.getInt('zoom');
 *    const locationId = query.get('id');
 *
 *    // Update the 'id' query parameter.
 *    const updateLocationId = (id: string) => setQuery({ id });
 */
export default function useQueryParams(): [QueryGetter, QueryUpdater] {
  const navigate = useNavigate();
  const location = useLocation();

  // Replace `query` every time `location.search` changes.  This way if the
  // caller is using `query` as a dependency to something like `useEffect()`,
  // they'll get a new query object and their effect will update.
  const query = useMemo(() => {
    const queryParams = new URLSearchParams(location.search);

    // Cache any array results, to make sure they are stable if we call `getAll()`
    // across multiple renders.
    const allCache: { [key: string]: string[] } = {};

    function getInt(param: string): number | undefined {
      let answer: number | undefined;
      const raw = queryParams.get(param);
      if (raw) {
        const parsed = parseInt(raw, 10);
        if (!isNaN(parsed)) {
          answer = parsed;
        }
      }
      return answer;
    }

    const getter: QueryGetter = {
      get: (param: string) => queryParams.get(param) ?? undefined,
      getAll: (param: string) => {
        if (!allCache[param]) {
          allCache[param] = queryParams.getAll(param);
        }
        return allCache[param];
      },
      getInt,
      getDate: (param: string) => {
        const str = queryParams.get(param);
        if (!str) {
          return undefined;
        }

        if (str.includes('-')) {
          const date = new Date(str);
          return isNaN(date.getTime()) ? undefined : date;
        }
        const parsed = parseInt(str, 10);
        if (!isNaN(parsed)) {
          return new Date(parsed);
        }

        return undefined;
      },
      getNumber: (param: string) => {
        let answer: number | undefined;
        const raw = queryParams.get(param);
        if (raw) {
          const parsed = parseFloat(raw);
          if (!isNaN(parsed)) {
            answer = parsed;
          }
        }
        return answer;
      },
      getBoolean: (param: string) => {
        const result = queryParams.get(param);
        if (result === null || result === undefined) {
          return undefined;
        }
        if (result.toLowerCase() === 'false') {
          return false;
        }
        return true;
      },
    };

    return getter;
  }, [location.search]);

  // This relies on `navigate and location` as a dependencies, which never change, so the
  // `updateQuery()` function will never change.  If the search changes, the
  // caller probably doesn't expect `updateQuery()` to change, since it's
  // "write only".
  const updateQuery = useCallback(
    (params: { [key: string]: string | number | Date | undefined }, replace?: boolean) => {
      const query = new URLSearchParams(location.search);
      for (const key of Object.keys(params)) {
        const value = params[key];
        if (value instanceof Date) {
          query.set(key, `${value.valueOf()}`);
        } else if (value !== undefined) {
          query.set(key, `${value}`);
        } else {
          query.delete(key);
        }
      }

      const url = `${location.pathname}?${query.toString()}`;
      if (replace) {
        navigate(url, { replace: true });
      } else {
        navigate(url);
      }
    },
    [location, navigate],
  );

  return [query, updateQuery];
}
